import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const INITIAL = {
  full_name: "", phone: "",
  property_address: "", property_city: "", property_state: "", property_zip: "",
  property_type: "single_family",
  estimated_value: "", mortgage_balance: "", monthly_payment: "",
  lender_name: "", mortgage_servicer: "", bank_agents: "", loan_number: "",
  loan_kind: "original", loan_backing: "private",
  sec_bundled: false, sec_bundle_notes: "",
  hoa_present: false, hoa_name: "",
  owns_land: true,
  has_lawyer: false, lawyer_name: "",
  courthouse_name: "", court_name: "", case_filed_date: "",
  law_firm_name: "", sheriff_location: "",
  sheriff_sale_scheduled: false, sheriff_sale_date: "",
  in_foreclosure: false, foreclosure_stage: "",
  notes: "",
};

export default function Apply() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        estimated_value: parseFloat(form.estimated_value) || 0,
        mortgage_balance: parseFloat(form.mortgage_balance) || 0,
        monthly_payment: parseFloat(form.monthly_payment) || 0,
      };
      await api.post("/applications", payload);
      toast.success("Application submitted — trustees notified.");
      navigate("/portal");
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="apply-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div>
          <span className="uppercase text-xs tracking-[0.25em] text-[color:var(--fire-red)]">Counterclaim Intake</span>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-none">House & mortgage application</h1>
          <p className="text-[color:var(--text-secondary)] mt-3">All details are required for the trustee audit. Stored in your encrypted Guild vault.</p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-8">
          <Section title="Owner">
            <Field label="Full name" required value={form.full_name} onChange={(v) => set("full_name", v)} testid="apply-full-name" />
            <Field label="Phone" required value={form.phone} onChange={(v) => set("phone", v)} testid="apply-phone" />
          </Section>

          <Section title="Property">
            <Field className="md:col-span-2" label="Street address" required value={form.property_address} onChange={(v) => set("property_address", v)} testid="apply-property-address" />
            <Field label="City" required value={form.property_city} onChange={(v) => set("property_city", v)} testid="apply-property-city" />
            <Field label="State" required value={form.property_state} onChange={(v) => set("property_state", v)} testid="apply-property-state" />
            <Field label="ZIP" required value={form.property_zip} onChange={(v) => set("property_zip", v)} testid="apply-property-zip" />
            <RadioRow label="Property type" value={form.property_type} onChange={(v) => set("property_type", v)}
              options={[
                ["single_family", "Single family"], ["condo", "Condo"], ["multi_family", "Multi-family"], ["land", "Land only"],
              ]} testidPrefix="apply-property-type" />
            <RadioRow label="Do you own the land?" value={form.owns_land ? "yes" : "no"} onChange={(v) => set("owns_land", v === "yes")}
              options={[["yes", "Yes"], ["no", "No"]]} testidPrefix="apply-owns-land" />
            <Field type="number" label="Estimated value (USD)" required value={form.estimated_value} onChange={(v) => set("estimated_value", v)} testid="apply-estimated-value" />
            <Field type="number" label="Monthly payment (USD)" required value={form.monthly_payment} onChange={(v) => set("monthly_payment", v)} testid="apply-monthly-payment" />
          </Section>

          <Section title="Mortgage & Lender">
            <Field type="number" className="md:col-span-2" label="Mortgage balance (USD)" required value={form.mortgage_balance} onChange={(v) => set("mortgage_balance", v)} testid="apply-mortgage-balance" />
            <Field label="Lender name (bank)" required value={form.lender_name} onChange={(v) => set("lender_name", v)} testid="apply-lender" />
            <Field label="Mortgage servicer" value={form.mortgage_servicer} onChange={(v) => set("mortgage_servicer", v)} testid="apply-servicer" />
            <Field label="Bank agents (names)" value={form.bank_agents} onChange={(v) => set("bank_agents", v)} testid="apply-bank-agents" />
            <Field label="Loan number" value={form.loan_number} onChange={(v) => set("loan_number", v)} testid="apply-loan-number" />
            <RadioRow label="Loan kind" value={form.loan_kind} onChange={(v) => set("loan_kind", v)}
              options={[["original", "Original loan"], ["refinance", "Refinance"]]} testidPrefix="apply-loan-kind" />
            <RadioRow label="Loan backing" value={form.loan_backing} onChange={(v) => set("loan_backing", v)}
              options={[["government_backed", "Government backed"], ["private", "Private lender"]]} testidPrefix="apply-loan-backing" />
            <CheckRow label="Listed in SEC mortgage bundles" checked={form.sec_bundled} onChange={(v) => set("sec_bundled", v)} testid="apply-sec-bundled" />
            {form.sec_bundled && (
              <Field className="md:col-span-2" label="SEC bundle notes / CUSIP / trust name" value={form.sec_bundle_notes} onChange={(v) => set("sec_bundle_notes", v)} testid="apply-sec-notes" />
            )}
          </Section>

          <Section title="HOA">
            <CheckRow label="HOA present on property" checked={form.hoa_present} onChange={(v) => set("hoa_present", v)} testid="apply-hoa-present" />
            {form.hoa_present && (
              <Field className="md:col-span-2" label="HOA name" value={form.hoa_name} onChange={(v) => set("hoa_name", v)} testid="apply-hoa-name" />
            )}
          </Section>

          <Section title="Legal & Court">
            <CheckRow label="I currently have a lawyer" checked={form.has_lawyer} onChange={(v) => set("has_lawyer", v)} testid="apply-has-lawyer" />
            {form.has_lawyer && (
              <Field className="md:col-span-2" label="Lawyer name & firm" value={form.lawyer_name} onChange={(v) => set("lawyer_name", v)} testid="apply-lawyer-name" />
            )}
            <Field label="Courthouse name" value={form.courthouse_name} onChange={(v) => set("courthouse_name", v)} testid="apply-courthouse" />
            <Field label="Court name (e.g. circuit court)" value={form.court_name} onChange={(v) => set("court_name", v)} testid="apply-court-name" />
            <Field type="date" label="Date case started in court" value={form.case_filed_date} onChange={(v) => set("case_filed_date", v)} testid="apply-case-date" />
            <Field label="Law firm representing bank" value={form.law_firm_name} onChange={(v) => set("law_firm_name", v)} testid="apply-law-firm" />
            <Field className="md:col-span-2" label="Sheriff location (office / county)" value={form.sheriff_location} onChange={(v) => set("sheriff_location", v)} testid="apply-sheriff-location" />
            <CheckRow label="Sheriff sale scheduled" checked={form.sheriff_sale_scheduled} onChange={(v) => set("sheriff_sale_scheduled", v)} testid="apply-sheriff-scheduled" />
            {form.sheriff_sale_scheduled && (
              <Field type="date" label="Sheriff sale date" value={form.sheriff_sale_date} onChange={(v) => set("sheriff_sale_date", v)} testid="apply-sheriff-date" />
            )}
            <CheckRow label="Currently in foreclosure" checked={form.in_foreclosure} onChange={(v) => set("in_foreclosure", v)} testid="apply-foreclosure-check" />
            {form.in_foreclosure && (
              <Field className="md:col-span-2" label="Foreclosure stage" value={form.foreclosure_stage} onChange={(v) => set("foreclosure_stage", v)} testid="apply-foreclosure-stage" />
            )}
          </Section>

          <Section title="Notes">
            <div className="md:col-span-2">
              <Label>Anything else trustees should know</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} data-testid="apply-notes" />
            </div>
          </Section>

          <Separator />
          <div className="flex items-center justify-between">
            <p className="text-sm text-[color:var(--text-secondary)]">
              Service fee (1.5% of mortgage balance, paid only at settlement) shown after submission.
            </p>
            <Button type="submit" disabled={busy} className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" data-testid="apply-submit-btn">
              {busy ? "Submitting…" : "Submit application"}
            </Button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card className="border-black/5">
      <CardHeader>
        <CardTitle className="font-serif text-2xl">{title}</CardTitle>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-4">{children}</CardContent>
    </Card>
  );
}
function Field({ label, value, onChange, required, type = "text", className = "", testid }) {
  return (
    <div className={className}>
      <Label>{label}{required && <span className="text-[color:var(--fire-red)]"> *</span>}</Label>
      <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
function RadioRow({ label, value, onChange, options, testidPrefix }) {
  return (
    <div className="md:col-span-2">
      <Label>{label}</Label>
      <RadioGroup value={value} onValueChange={onChange} className="flex flex-wrap gap-2 mt-2">
        {options.map(([v, l]) => (
          <label key={v} className="flex items-center gap-2 border border-black/10 rounded-md px-3 py-2 cursor-pointer hover:border-[color:var(--orange)]" data-testid={`${testidPrefix}-${v}`}>
            <RadioGroupItem value={v} />
            <span className="text-sm">{l}</span>
          </label>
        ))}
      </RadioGroup>
    </div>
  );
}
function CheckRow({ label, checked, onChange, testid }) {
  return (
    <label className="md:col-span-2 flex items-center gap-3 cursor-pointer" data-testid={testid}>
      <Checkbox checked={checked} onCheckedChange={onChange} />
      <span className="text-sm">{label}</span>
    </label>
  );
}
